<template>
  <div class="kpi" :style="{ '--kpi-a': tile.colors[0], '--kpi-b': tile.colors[1] }">
    <div class="kpi-glow"></div>
    <div class="kpi-top">
      <span class="kpi-icon"><i :class="icon"></i></span>
      <span class="kpi-label">{{ tile.label }}</span>
    </div>
    <div class="kpi-value" v-if="tile.value !== null">
      <animated-number :value="tile.value" :decimals="tile.decimals" locale="en-GB" :duration="1600"/>
      <small v-if="tile.unit">{{ tile.unit }}</small>
    </div>
    <div class="kpi-value kpi-none" v-else-if="loaded" title="No figure yet">—</div>
    <div class="kpi-value kpi-skeleton" v-else></div>
    <div class="kpi-sub" v-if="tile.sub">{{ tile.sub }}</div>
    <div class="kpi-spark" v-if="spark.length > 2">
      <apexchart :key="chartKey" type="area" height="100%" :options="sparkOptions" :series="[{ name: tile.label, data: spark }]"/>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import AnimatedNumber from '../AnimatedNumber.vue';
import { apexBase, compact, fmt, timeAgo, themeState } from '../theme';

// gradient of each metric (start, end)
const COLORS = {
  co2: ['#2fd0c4', '#159895'], yearly: ['#5fb4f0', '#2f7fc4'], trees: ['#9ed77f', '#3d7a3f'], forests: ['#6bb15e', '#2f6b3f'],
  available: ['#f4c25b', '#d08a1e'], retired: ['#f2875f', '#c4532f'], customers: ['#a594f9', '#6a4fe0'], scans: ['#f27aa3', '#c23f72'],
  countries: ['#5fb4f0', '#3b6fd4'], species: ['#e0a25a', '#b76d2a'], splits: ['#7cc768', '#159895'],
};

export default {
  name: 'KpiTile',
  components: { apexchart: VueApexCharts, AnimatedNumber },
  props: {
    metric: { type: String, required: true },
    stats: { type: Object, required: true },
    icon: { type: String, default: 'fas fa-chart-bar' }
  },
  computed: {
    // the data has arrived (a missing figure is then "—", not an endless skeleton)
    loaded() { return !!(this.stats.overview && this.stats.insights); },
    // a theme or data change re-creates the chart: ApexCharts does not always redraw mixed charts on update
    chartKey() { return `${themeState.tick}:${JSON.stringify(this.spark)}`; },
    tile() {
      const o = this.stats.overview || {};
      const i = this.stats.insights || {};
      const tf = this.stats.tokenFlow || {};
      const has = (v) => v !== undefined && v !== null;
      const t = { label: '', value: null, decimals: 0, unit: '', sub: '', colors: COLORS[this.metric] || COLORS.co2 };
      switch (this.metric) {
        case 'co2': {
          const v = i.totals ? i.totals.co2 : null;
          Object.assign(t, { label: 'CO2 stored', value: has(v) ? v : null, unit: 't', sub: i.totals ? `in ${fmt(i.totals.forests)} forests` : '' });
          break;
        }
        case 'yearly': {
          const v = has(o.totalTheoreticalYearlyIncrement) ? Number(o.totalTheoreticalYearlyIncrement) : null;
          Object.assign(t, { label: 'CO2 captured per year', value: v, unit: 't/yr', decimals: 0, sub: v ? `≈ ${fmt(v / 365, 1)} t every day` : '' });
          break;
        }
        case 'trees':
          Object.assign(t, { label: 'Trees', value: has(o.totalTrees) ? Number(o.totalTrees) : null, sub: has(o.treeTypesQty) ? `${fmt(o.treeTypesQty)} species` : '' });
          break;
        case 'forests':
          Object.assign(t, { label: 'Forests', value: has(o.areasQty) ? Number(o.areasQty) : null, sub: has(o.totalArea) ? `${fmt(o.totalArea, 1)} hectares` : '' });
          break;
        case 'available':
          Object.assign(t, { label: 'ZCO2 available', value: has(tf.totalAvailable) ? Number(tf.totalAvailable) : null, sub: 'generated, not yet delivered' });
          break;
        case 'retired': {
          const r = i.retirements;
          Object.assign(t, { label: 'ZCO2 retired', value: r ? r.total : null, decimals: 2, sub: r ? `${fmt(r.count)} retirements` : '' });
          break;
        }
        case 'customers': {
          const trend = this.stats.customersTrend || [];
          const last = trend.length ? trend[trend.length - 1] : null;
          Object.assign(t, { label: 'Customers', value: has(o.customersQty) ? Number(o.customersQty) : null, sub: last ? `+${fmt(last.newCustomers)} in their last month` : '' });
          break;
        }
        case 'scans':
          Object.assign(t, { label: 'Forest scans', value: has(o.checkpointsQty) ? Number(o.checkpointsQty) : null, sub: o.lastScanDt ? `last one ${timeAgo(o.lastScanDt)}` : '' });
          break;
        case 'countries': {
          const top = (this.stats.byCountry || [])[0];
          Object.assign(t, { label: 'Countries', value: has(o.countriesQty) ? Number(o.countriesQty) : null, sub: top ? `led by ${top.country}` : '' });
          break;
        }
        case 'species': {
          const top = (this.stats.byTreeType || [])[0];
          Object.assign(t, { label: 'Tree species', value: has(o.treeTypesQty) ? Number(o.treeTypesQty) : null, sub: top ? `mostly ${top.treeType}` : '' });
          break;
        }
        case 'splits': {
          const s = i.trees;
          Object.assign(t, { label: 'Splits delivered', value: s ? s.splitsDelivered : null, sub: i.companies ? `to ${fmt(i.companies.length)} ${i.companies.length === 1 ? 'company' : 'companies'}` : '' });
          break;
        }
      }
      return t;
    },
    spark() {
      const i = this.stats.insights || {};
      const timeline = (i.timeline || []).slice(-18);
      switch (this.metric) {
        case 'co2': return (i.stored || []).slice(-24).map((d) => Math.round(d.co2));
        case 'trees': return (i.stored || []).slice(-24).map((d) => d.trees);
        case 'forests': return (i.stored || []).slice(-24).map((d) => d.forests);
        case 'customers': return (this.stats.customersTrend || []).slice(-24).map((d) => d.cumulativeCustomers);
        case 'yearly': return ((i.forecast && i.forecast.years) || []).filter((y) => y.year <= i.forecast.currentYear + 10).map((y) => y.increment);
        case 'retired': return timeline.map((m) => m.retired);
        case 'splits': return timeline.map((m) => m.splits);
        case 'scans': return timeline.map((m) => m.scans);
        default: return [];
      }
    },
    sparkOptions() {
      void themeState.tick;
      const [a] = this.tile.colors;
      return apexBase({
        chart: { sparkline: { enabled: true }, animations: { speed: 1400 } },
        colors: [a],
        stroke: { width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.02, stops: [0, 100] } },
        tooltip: { enabled: true, x: { show: false }, y: { formatter: (v) => compact(v), title: { formatter: () => '' } }, marker: { show: false } },
      });
    }
  }
};
</script>

<style scoped lang="scss">
.kpi {
  position: relative;
  height: 100%;
  border-radius: 20px;
  padding: 16px 18px 0;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.06));
  box-shadow: var(--shadow-md);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.35s cubic-bezier(.2, .8, .2, 1), box-shadow 0.35s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 40px -14px var(--kpi-a);
    .kpi-glow { opacity: 0.32; transform: scale(1.15); }
  }
}

.kpi-glow {
  position: absolute;
  width: 180px;
  height: 180px;
  right: -60px;
  top: -70px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--kpi-a) 0%, transparent 70%);
  opacity: 0.2;
  transition: opacity 0.5s, transform 0.8s;
  pointer-events: none;
}

.kpi-top {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}

.kpi-icon {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.9rem;
  background: linear-gradient(135deg, var(--kpi-a), var(--kpi-b));
  box-shadow: 0 6px 16px -6px var(--kpi-b);
}

.kpi-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.kpi-value {
  position: relative;
  margin-top: 8px;
  font-family: 'Sora', 'Plus Jakarta Sans', sans-serif;
  font-size: clamp(1.4rem, 2.1vw, 2rem);
  font-weight: 800;
  line-height: 1.1;
  color: var(--text-heading);
  font-variant-numeric: tabular-nums;

  small {
    font-size: 0.5em;
    font-weight: 700;
    color: var(--text-muted);
    margin-left: 4px;
  }
}

.kpi-none { color: var(--text-muted); }

.kpi-skeleton {
  height: 30px;
  width: 60%;
  border-radius: 8px;
  background: linear-gradient(90deg, var(--border-color) 0%, var(--bg-secondary) 50%, var(--border-color) 100%);
  background-size: 200% 100%;
  animation: kpi-shimmer 1.4s infinite;
}

.kpi-sub {
  position: relative;
  font-size: 0.78rem;
  color: var(--text-muted);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi-spark {
  flex: 1 1 auto;
  min-height: 24px;
  margin: 4px -18px 0;
}

@keyframes kpi-shimmer {
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
}
</style>
